/**
 * A register's status views (D278, the approved redesign's view tabs).
 *
 * A row of tabs over one list, each the same server-side status filter the
 * register already had. Built here rather than in the shared library because
 * only this module needs it so far; it is an ordinary tablist, so arrow keys
 * are not required — each tab is a plain button in the tab order, and the
 * selected one says so with `aria-selected`.
 *
 * Underlined, not filled: a filled tab reads as a primary action, and the
 * page's one primary action is "Receive samples".
 */
export function StatusViews<Value extends string>({
  label,
  value,
  options,
  onChange,
}: {
  /** Names the tablist. Already translated. */
  readonly label: string;
  readonly value: Value | null;
  readonly options: readonly { readonly value: Value | null; readonly label: string }[];
  readonly onChange: (value: Value | null) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="mb-stack flex flex-wrap gap-x-1 border-b border-default-200"
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value ?? '__all'}
            type="button"
            role="tab"
            aria-selected={isSelected}
            onClick={() => {
              if (!isSelected) onChange(option.value);
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-body outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 ${
              isSelected
                ? 'border-primary-600 font-semibold text-default-900'
                : 'border-transparent text-default-600 hover:text-default-900'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
